import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  FileText,
  CheckCircle2,
  Clock,
  Eye,
  Play,
  Search,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  Trash2,
  TrendingDown,
  TrendingUp,
  DollarSign,
  FolderOpen,
  History,
  MoreVertical,
  Download,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AssetSelectionDialog } from '@/components/depreciation/asset-selection-dialog';
import { EvoFeatureFeedbackCollector } from '@/components/FeedbackCollector';
import type { Asset } from '@/types/asset';
import { dataService, getDataSourceMode } from '@/services/data-service';
import { formatCurrency } from '@/lib/format-currency';
import { mockDisposalBatches, type DisposalBatch, type DisposalItem } from '@/lib/mock-disposals-data';
import { exportToCSV, exportToExcel, generateFilename, formatCurrencyForExport, type ExportColumn } from '@/lib/export-utils';
import { KpiCard } from '@/components/kpi-card';
import { DisposalEntryScreen } from '@/components/transactions/disposal-entry-screen';
import { useAssetPermissions } from '@/hooks/use-permissions';

const getDisposalExportColumns = (): ExportColumn<DisposalItem>[] => [
  { label: 'Asset Code', key: 'assetCode' as keyof DisposalItem },
  { label: 'Asset Description', key: 'assetDescription' as keyof DisposalItem },
  { label: 'Category', key: 'category' as keyof DisposalItem },
  { label: 'Location', key: 'location' as keyof DisposalItem },
  { label: 'Current Cost', key: 'currentCost' as keyof DisposalItem, format: (val: number) => formatCurrencyForExport(val) },
  { label: 'Current Depreciation', key: 'currentDepreciation' as keyof DisposalItem, format: (val: number) => formatCurrencyForExport(val) },
  { label: 'Current NBV', key: 'currentNBV' as keyof DisposalItem, format: (val: number) => formatCurrencyForExport(val) },
  { label: 'Proceeds', key: 'proceedsValue' as keyof DisposalItem, format: (val: number) => formatCurrencyForExport(val) },
  { label: 'Gain/Loss', key: 'gainLoss' as keyof DisposalItem, format: (val: number) => formatCurrencyForExport(val) },
  { label: 'Notes', key: 'notes' as keyof DisposalItem },
];

const getDataSource = getDataSourceMode;

type SortDirection = 'asc' | 'desc' | null;

interface SortIconProps {
  readonly columnKey: string;
  readonly sortKey: string | null;
  readonly sortDirection: SortDirection;
}

function SortIcon({ columnKey, sortKey, sortDirection }: SortIconProps): React.ReactElement | null {
  if (sortKey !== columnKey) return null;
  return sortDirection === 'asc' ? (
    <ArrowUp className="ml-2 h-4 w-4" />
  ) : (
    <ArrowDown className="ml-2 h-4 w-4" />
  );
}

// Helper function to get sort value for a batch
function getDisposalBatchSortValue(batch: DisposalBatch, sortKey: string): string | number {
  switch (sortKey) {
    case 'reference':
      return batch.batchReference;
    case 'date':
      return new Date(batch.dateOfRun).getTime();
    case 'periodType':
      return batch.periodType;
    case 'totalValue':
      return batch.totalDisposalValue;
    case 'proceeds':
      return batch.totalProceedsValue;
    case 'gainLoss':
      return batch.totalGainLoss;
    case 'postedBy':
      return batch.postedBy ?? '';
    case 'postedDate':
      return batch.postedDate ? new Date(batch.postedDate).getTime() : 0;
    case 'status':
      return batch.status;
    default:
      return 0;
  }
}

// Helper function to compare sort values
function compareDisposalSortValues(aValue: string | number, bValue: string | number, direction: 'asc' | 'desc'): number {
  if (aValue === null || aValue === undefined) return 1;
  if (bValue === null || bValue === undefined) return -1;

  if (direction === 'asc') {
    if (aValue > bValue) return 1;
    if (aValue < bValue) return -1;
    return 0;
  }
  // direction === 'desc'
  if (aValue < bValue) return 1;
  if (aValue > bValue) return -1;
  return 0;
}

// Helper function to generate page numbers for pagination
function generatePageNumbers(currentPage: number, totalPages: number): (number | string)[] {
  const pageNumbers: (number | string)[] = [];
  const maxVisiblePages = 7;
  
  if (totalPages <= maxVisiblePages) {
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(i);
    }
  } else {
    pageNumbers.push(1);
    
    if (currentPage > 3) {
      pageNumbers.push('...');
    }
    
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    
    for (let i = start; i <= end; i++) {
      pageNumbers.push(i);
    }
    
    if (currentPage < totalPages - 2) {
      pageNumbers.push('...');
    }
    
    pageNumbers.push(totalPages);
  }
  
  return pageNumbers;
}

// Helper function to render a single page button
function renderPageButton(
  page: number | string,
  pageIndex: number,
  pageNumbers: (number | string)[],
  currentPage: number,
  setCurrentPage: (page: number) => void
): React.ReactElement {
  if (page === '...') {
    return (
      <span key={`ellipsis-before-${pageNumbers[pageIndex + 1]}`} className="px-2 text-muted-foreground">
        ...
      </span>
    );
  }
  
  return (
    <Button
      key={`page-${page}`}
      variant={currentPage === page ? 'default' : 'outline'}
      size="sm"
      onClick={() => setCurrentPage(page as number)}
      className="min-w-[36px]"
    >
      {page}
    </Button>
  );
}

function DisposalsPageContent(): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const { canDelete } = useAssetPermissions();
  
  const [showAssetSelection, setShowAssetSelection] = useState(false);
  const [currentBatch, setCurrentBatch] = useState<DisposalBatch | null>(null);
  const [completedBatches, setCompletedBatches] = useState<DisposalBatch[]>(mockDisposalBatches);
  const [isLoadingBatches, setIsLoadingBatches] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState<{ key: string | null; direction: 'asc' | 'desc' | null }>({
    key: null,
    direction: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const pageSize = 25;

  useEffect(() => {
    const fetchBatches = async () => {
      setIsLoadingBatches(true);
      try {
        if (getDataSource() === 'demo') {
          setCompletedBatches(mockDisposalBatches);
        } else {
          const response = await dataService.getDisposalTransactions({
            pageSize: 100,
            sortBy: 'transactionDate',
            order: 'desc',
          });
          if (response?.items && response.items.length > 0) {
            const batchMap = new Map<string, DisposalBatch>();
            response.items.forEach((transaction: any) => {
              const batchRef = transaction.reference || transaction.id;
              if (!batchMap.has(batchRef)) {
                batchMap.set(batchRef, {
                  id: transaction.id,
                  batchReference: batchRef,
                  dateOfRun: transaction.transactionDate || transaction.createdDate,
                  periodType: transaction.year < new Date().getFullYear() ? 'service' : 'current',
                  status: 'Completed',
                  disposeOfMode: 'value',
                  notes: transaction.notes || '',
                  items: [],
                  preparedBy: transaction.createdBy || 'System',
                  preparedDate: transaction.createdDate,
                  postedBy: transaction.postedBy,
                  postedDate: transaction.postedDate,
                  totalDisposalValue: transaction.movementValue || 0,
                  totalProceedsValue: transaction.proceedsValue || 0,
                  totalGainLoss: transaction.gainLoss || 0,
                  currency: 'GBP',
                  createdBy: transaction.createdBy || 'System',
                  createdDate: transaction.createdDate,
                  modifiedBy: transaction.modifiedBy,
                  modifiedDate: transaction.modifiedDate,
                });
              }
            });
            const batches = Array.from(batchMap.values()).sort((a, b) => 
              new Date(b.dateOfRun).getTime() - new Date(a.dateOfRun).getTime()
            );
            setCompletedBatches(batches);
          } else {
            setCompletedBatches([]);
          }
        }
      } catch (error) {
        console.error('Error fetching disposal batches:', error);
        if (getDataSource() === 'demo') {
          setCompletedBatches(mockDisposalBatches);
        } else {
          setCompletedBatches([]);
        }
      } finally {
        setIsLoadingBatches(false);
      }
    };

    if (!currentBatch) {
      fetchBatches();
    }
  }, [currentBatch, refreshTrigger]);

  // Handle URL parameters and navigation
  useEffect(() => {
    const state = location.state as { selectedAssets?: Asset[]; refresh?: boolean; viewBatch?: boolean } | null;
    const searchParams = new URLSearchParams(location.search);
    const batchId = searchParams.get('batch');
    
    // Handle refresh from breadcrumb click
    if (state?.refresh && !batchId) {
      setCurrentBatch(null);
      setSearchQuery('');
      setSortConfig({ key: null, direction: null });
      setRefreshTrigger(prev => prev + 1);
      window.history.replaceState({}, document.title);
      return;
    }
    
    // Handle clearing batch when navigating back to main page
    if (!batchId && currentBatch) {
      setCurrentBatch(null);
      setRefreshTrigger(prev => prev + 1);
      return;
    }
    
    if (state?.selectedAssets && state.selectedAssets.length > 0) {
      handleAssetsSelected(state.selectedAssets);
      window.history.replaceState({}, document.title);
    }
  }, [location.state, location.search]);

  // Reset pagination when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleStartRun = () => {
    setShowAssetSelection(true);
  };

  const handleViewLastBatchSummary = () => {
    if (!completedBatches || completedBatches.length === 0) {
      toast.error('No batch history available', { 
        description: 'There are no completed batches to view.' 
      });
      return;
    }

    const lastBatch = completedBatches[0]; // Already sorted by date descending
    
    navigate(`/transactions/disposals?batch=${lastBatch.id}`, {
      state: { viewBatch: true }
    });
    
    setCurrentBatch(lastBatch);
    
    toast.success('Batch loaded', { 
      description: `Viewing batch from ${lastBatch.dateOfRun}` 
    });
  };

  const handleAssetsSelected = (selectedAssets: Asset[]) => {
    // Create a new batch with selected assets
    const now = new Date().toISOString();
    const newBatch: DisposalBatch = {
      id: `batch-${Date.now()}`,
      batchReference: '',
      dateOfRun: new Date().toISOString().split('T')[0],
      periodType: 'current',
      status: 'In Progress',
      disposeOfMode: 'value',
      notes: '',
      items: selectedAssets.map((asset, index) => ({
        id: `disp-item-${Date.now()}-${index}`,
        assetId: asset.id,
        assetCode: asset.code,
        assetDescription: asset.name,
        category: asset.category ?? '',
        location: asset.location ?? '',
        currentCost: asset.acquisitionCost ?? 0,
        currentDepreciation: asset.accumulatedDepreciation ?? 0,
        currentNBV: asset.netBookValue ?? 0,
        historicalCost: asset.acquisitionCost ?? 0,
        historicalDepreciation: asset.accumulatedDepreciation ?? 0,
        historicalNBV: asset.netBookValue ?? 0,
        disposalCost: 0,
        disposalDepreciation: 0,
        disposalNBV: 0,
        proceedsValue: 0,
        gainLoss: 0,
        notes: '',
      })),
      preparedBy: 'Current User',
      preparedDate: now,
      totalDisposalValue: 0,
      totalProceedsValue: 0,
      totalGainLoss: 0,
      currency: 'GBP',
      createdBy: 'Current User',
      createdDate: now,
      modifiedBy: 'Current User',
      modifiedDate: now,
    };
    
    setCurrentBatch(newBatch);
    setShowAssetSelection(false);
  };

  // Sorting handler
  const handleSort = (key: string) => {
    setSortConfig((current) => {
      if (current.key === key) {
        // Cycle through: asc -> desc -> null
        if (current.direction === 'asc') {
          return { key, direction: 'desc' };
        } else if (current.direction === 'desc') {
          return { key: null, direction: null };
        }
      }
      // Start with ascending for new column
      return { key, direction: 'asc' };
    });
    setCurrentPage(1);
  };

  // Filter and sort batches
  const filteredBatches = completedBatches
    .filter((batch) => {
      if (!searchQuery.trim()) return true;
      
      const query = searchQuery.toLowerCase();
      return (
        batch.batchReference.toLowerCase().includes(query) ||
        batch.notes.toLowerCase().includes(query) ||
        batch.preparedBy.toLowerCase().includes(query) ||
        batch.postedBy?.toLowerCase().includes(query) ||
        batch.status.toLowerCase().includes(query) ||
        batch.disposalReasonCode?.toLowerCase().includes(query) ||
        batch.disposalReasonDescription?.toLowerCase().includes(query)
      );
    })
    .sort((a, b) => {
      if (!sortConfig.key || !sortConfig.direction) return 0;

      const aValue = getDisposalBatchSortValue(a, sortConfig.key);
      const bValue = getDisposalBatchSortValue(b, sortConfig.key);
      return compareDisposalSortValues(aValue, bValue, sortConfig.direction);
    });

  // If viewing a batch, show the entry screen
  if (currentBatch) {
    return (
      <DisposalEntryScreen
        batch={currentBatch}
        onClose={() => {
          setCurrentBatch(null);
          navigate('/transactions/disposals');
        }}
        onSave={(updatedBatch) => {
          setCurrentBatch(updatedBatch);
          toast.success('Batch saved successfully');
        }}
      />
    );
  }

  // Landing Dashboard View
  return (
    <div className="flex h-full flex-col">
      {/* Page Header */}
      <div className="border-b bg-card p-6 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Trash2 className="h-6 w-6 text-primary" />
              Disposals
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage disposal of assets where they have been discarded or sold
            </p>
          </div>
          <div className="flex items-center gap-2">
            <EvoFeatureFeedbackCollector feature="Disposal Transactions" />
            <Button variant="outline" onClick={handleViewLastBatchSummary}>
              <Eye className="mr-2 h-4 w-4" />
              View Last Batch Summary
            </Button>
            {canDelete && (
              <Button onClick={handleStartRun}>
                <Play className="mr-2 h-4 w-4" />
                Start New Disposal Batch
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* KPI Cards */}
        <div className="p-6 pb-0 flex-shrink-0">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-3">
            <KpiCard
              icon={TrendingDown}
              title="Total Disposals (YTD)"
              value={formatCurrency(completedBatches.reduce((sum, batch) => sum + batch.totalDisposalValue, 0))}
              description="Year-to-date disposal value"
            />
            <KpiCard
              icon={DollarSign}
              title="Total Proceeds (YTD)"
              value={formatCurrency(completedBatches.reduce((sum, batch) => sum + batch.totalProceedsValue, 0))}
              description="Year-to-date proceeds from disposals"
            />
            <KpiCard
              icon={TrendingUp}
              title="Net Gain/Loss (YTD)"
              value={formatCurrency(completedBatches.reduce((sum, batch) => sum + batch.totalGainLoss, 0))}
              description="Year-to-date gain or loss on disposals"
            />
          </div>
        </div>

        {/* Transaction History Header */}
        <div className="bg-white dark:bg-black border-t border-b border-slate-200 dark:border-slate-800 px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold">Transaction History</h2>
              <p className="text-sm text-muted-foreground mt-1">All disposal batches (in progress and completed)</p>
            </div>
            <div className="relative w-80">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search transactions..."
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchQuery(e.target.value)}
                className="pl-9 bg-white dark:bg-slate-900"
              />
            </div>
          </div>
        </div>

        {/* Table Container */}
        <div className="flex-1 flex flex-col min-h-0">
          {(() => {
            // Pagination calculations
            const totalPages = Math.ceil(filteredBatches.length / pageSize);
            const startIndex = (currentPage - 1) * pageSize;
            const endIndex = startIndex + pageSize;
            const paginatedBatches = filteredBatches.slice(startIndex, endIndex);

            if (isLoadingBatches) {
              return (
                <div className="flex h-full items-center justify-center py-12">
                  <div className="text-center">
                    <p className="text-lg font-medium">Loading batches...</p>
                  </div>
                </div>
              );
            }

            if (filteredBatches.length === 0) {
              return (
                <div className="flex flex-col items-center justify-center space-y-4 py-12">
                  <div className="rounded-full bg-slate-100 dark:bg-slate-800 p-6">
                    <FileText className="h-12 w-12 text-slate-400 dark:text-slate-500" />
                  </div>
                  <div className="space-y-2 text-center">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">No transactions found</h3>
                    <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                      {searchQuery ? "Try adjusting your search to find what you're looking for" : 'No batches found'}
                    </p>
                  </div>
                </div>
              );
            }

            return (
              <div className="flex-1 flex flex-col min-h-0">
                <div className="flex-1 overflow-auto">
                  <Table className="w-full">
                    <TableHeader className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
                      <TableRow className="border-b-2 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
                        <TableHead className="h-12 font-semibold">
                          <button
                            onClick={() => handleSort('reference')}
                            className="flex items-center hover:bg-slate-200 dark:hover:bg-slate-700 px-2 py-1 rounded transition-colors"
                          >
                            Reference
                            <SortIcon columnKey="reference" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                          </button>
                        </TableHead>

                        <TableHead className="h-12 font-semibold">
                          <button
                            onClick={() => handleSort('date')}
                            className="flex items-center hover:bg-slate-200 dark:hover:bg-slate-700 px-2 py-1 rounded transition-colors"
                          >
                            Date
                            <SortIcon columnKey="date" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                          </button>
                        </TableHead>
                        <TableHead className="h-12 font-semibold">Disposal Reason</TableHead>
                        <TableHead className="h-12 font-semibold">
                          <button
                            onClick={() => handleSort('periodType')}
                            className="flex items-center hover:bg-slate-200 dark:hover:bg-slate-700 px-2 py-1 rounded transition-colors"
                          >
                            Period Type
                            <SortIcon columnKey="periodType" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                          </button>
                        </TableHead>
                        <TableHead className="h-12 font-semibold text-right">
                          <button
                            onClick={() => handleSort('totalValue')}
                            className="flex items-center ml-auto hover:bg-slate-200 dark:hover:bg-slate-700 px-2 py-1 rounded transition-colors"
                          >
                            Disposal Value
                            <SortIcon columnKey="totalValue" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                          </button>
                        </TableHead>
                        <TableHead className="h-12 font-semibold text-right">
                          <button
                            onClick={() => handleSort('proceeds')}
                            className="flex items-center ml-auto hover:bg-slate-200 dark:hover:bg-slate-700 px-2 py-1 rounded transition-colors"
                          >
                            Proceeds
                            <SortIcon columnKey="proceeds" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                          </button>
                        </TableHead>
                        <TableHead className="h-12 font-semibold text-right">
                          <button
                            onClick={() => handleSort('gainLoss')}
                            className="flex items-center ml-auto hover:bg-slate-200 dark:hover:bg-slate-700 px-2 py-1 rounded transition-colors"
                          >
                            Gain/Loss
                            <SortIcon columnKey="gainLoss" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                          </button>
                        </TableHead>
                        <TableHead className="h-12 font-semibold">
                          <button
                            onClick={() => handleSort('postedBy')}
                            className="flex items-center hover:bg-slate-200 dark:hover:bg-slate-700 px-2 py-1 rounded transition-colors"
                          >
                            Posted By
                            <SortIcon columnKey="postedBy" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                          </button>
                        </TableHead>
                        <TableHead className="h-12 font-semibold">
                          <button
                            onClick={() => handleSort('status')}
                            className="flex items-center hover:bg-slate-200 dark:hover:bg-slate-700 px-2 py-1 rounded transition-colors"
                          >
                            Status
                            <SortIcon columnKey="status" sortKey={sortConfig.key} sortDirection={sortConfig.direction} />
                          </button>
                        </TableHead>
                        <TableHead className="h-12 font-semibold text-right"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedBatches.map((batch, index) => (
                        <TableRow 
                          key={batch.id} 
                          className={`
                            ${index % 2 === 0 ? 'bg-white dark:bg-slate-950' : 'bg-slate-100 dark:bg-slate-900'}
                            hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors border-b border-slate-200 dark:border-slate-800
                          `}
                        >
                          <TableCell className="font-semibold">{batch.batchReference}</TableCell>
                          <TableCell>{new Date(batch.dateOfRun).toLocaleDateString('en-GB')}</TableCell>
                          <TableCell>
                            {batch.disposalReasonCode ? (
                              <div>
                                <div className="font-medium">{batch.disposalReasonCode}</div>
                                <div className="text-xs text-muted-foreground">{batch.disposalReasonDescription}</div>
                              </div>
                            ) : '-'}
                          </TableCell>
                          <TableCell>{batch.periodType === 'current' ? 'Current Period' : 'Service Period'}</TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(batch.totalDisposalValue)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(batch.totalProceedsValue)}
                          </TableCell>
                          <TableCell className={`text-right font-medium ${batch.totalGainLoss >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {formatCurrency(batch.totalGainLoss)}
                          </TableCell>
                          <TableCell>{batch.postedBy ?? '-'}</TableCell>
                          <TableCell>
                            <Badge 
                              variant="secondary"
                              className={
                                batch.status === 'Completed'
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                  : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                              }
                            >
                              {batch.status === 'Completed' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                              {batch.status === 'In Progress' && <Clock className="h-3 w-3 mr-1" />}
                              {batch.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => {
                                  setCurrentBatch(batch);
                                }}
                              >
                                <FolderOpen className="h-4 w-4 mr-2" />
                                Open
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => {
                                  navigate(`/enquiry?reference=${encodeURIComponent(batch.batchReference)}&transactionType=Disposal`);
                                }}
                                title="View this transaction in Transaction History"
                              >
                                <History className="h-4 w-4 mr-2" />
                                View Transaction History
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="outline" size="icon">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => {
                                    const items = batch.items as DisposalItem[] | undefined;
                                    if (!items || items.length === 0) { toast.error('No data to export'); return; }
                                    exportToCSV({ data: items, columns: getDisposalExportColumns(), filename: generateFilename(`disposal-batch-${batch.batchReference || 'draft'}`, 'csv') });
                                  }}>
                                    <Download className="mr-2 h-4 w-4" />
                                    Export CSV
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => {
                                    const items = batch.items as DisposalItem[] | undefined;
                                    if (!items || items.length === 0) { toast.error('No data to export'); return; }
                                    exportToExcel({ data: items, columns: getDisposalExportColumns(), filename: generateFilename(`disposal-batch-${batch.batchReference || 'draft'}`, 'xlsx'), sheetName: 'Disposal Details' });
                                  }}>
                                    <Download className="mr-2 h-4 w-4" />
                                    Export Excel
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                
                {/* Pagination Footer */}
                <div className="flex items-center justify-between gap-4 px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-black">
                  <div className="text-sm text-muted-foreground flex-shrink-0">
                    Showing {startIndex + 1}-{Math.min(endIndex, filteredBatches.length)} of {filteredBatches.length} transactions
                    {filteredBatches.length !== completedBatches.length && ` (filtered from ${completedBatches.length} total)`}
                  </div>
                  
                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                      >
                        First
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Previous
                      </Button>
                      
                      {/* Page Number Buttons */}
                      <div className="flex items-center gap-1">
                        {generatePageNumbers(currentPage, totalPages).map((page, pageIndex, pageNumbers) =>
                          renderPageButton(page, pageIndex, pageNumbers, currentPage, setCurrentPage)
                        )}
                      </div>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                      >
                        Last
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Asset Selection Dialog */}
      {showAssetSelection && (
        <AssetSelectionDialog
          open={showAssetSelection}
          onOpenChange={setShowAssetSelection}
          onConfirm={handleAssetsSelected}
          title="Select Assets for Disposal"
          description="Choose the assets you want to dispose of. You can select multiple assets to create a disposal batch."
          confirmButtonLabel="Add to Disposal Batch"
        />
      )}
    </div>
  );
}

export default function DisposalsPage(): React.ReactElement {
  return <DisposalsPageContent />;
}
